var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Flex, Container, Box, useDisclosure } from 'shared/components/ui';
import { Calendar, CalendarFilled, Document, DocumentFilled, Eye, Settings, SettingsFilled, SignOut, Financial, FinancialFilled, MemberList, MemberListFilled, } from 'shared/assets/icons';
import { PrintWrapper, SideNav } from 'shared/components';
import React, { useEffect, useRef } from 'react';
import { useFlagEnabled } from 'flagging/hooks';
import { ViewAsFrame } from '../../shared/components/features/SideNav/viewAsFrame';
import { ViewAsInputBox } from '../../shared/components/features/SideNav/viewAsInputBox';
import { useComponentVisible } from 'shared/hooks/useComponentVisible';
import { Actions, Roles, Subjects } from 'constants/accessControlList';
import { useRbac } from 'shared/features/rbac';
import { Logout } from 'shared/features/auth';
import { useViewAs } from 'shared/hooks';
import { useExperienceState } from 'src/shared/features/experience-state';
import { useContractProviderPayFlag } from 'src/shared/features/ProviderPay';
import { PowerPanelProvider } from 'shared/features/power-panel';
import { useCanIEditPrescriptions } from 'shared/features/erx';
import { useErxNavItem } from './hooks';
import { IFramePageProvider, IFrameOverlay } from 'shared/features/iframe-page';
import { zenDeskSidebarItem } from 'src/shared/features/zendesk-help';
/**
 * Display the contents with the default layout of using a side navbar and a container
 *
 * @returns {React.ReactNode}
 */
export var SidebarLayout = function () {
    var pathname = useLocation().pathname;
    var navigate = useNavigate();
    var schedulingFlagEnabled = useFlagEnabled('SHOW_SCHEDULING_ON_SIDEBAR');
    var settingsFlagEnabled = useFlagEnabled('SHOW_SETTINGS_ON_SIDEBAR');
    var viewAsFlagEnabled = useFlagEnabled('SHOW_ACT_AS_ON_SIDEBAR');
    var enableMemberSearch = useFlagEnabled('ENABLE_MEMBER_SEARCH');
    var enableMedicationsTab = useFlagEnabled('ENABLE_MEDICATIONS_TAB');
    var medManagerNotesFlagEnabled = useFlagEnabled('MED_MANAGER_CAN_EDIT_NOTES');
    var isNotesRoute = pathname.split('/').includes('notes');
    var _a = useExperienceState(), experienceState = _a.data, isFetched = _a.isFetched;
    var providerPayEnabled = useContractProviderPayFlag(isFetched, experienceState === null || experienceState === void 0 ? void 0 : experienceState.ehrFeatures);
    var _b = useViewAs(), viewAsEnabled = _b.viewAsEnabled, viewAsData = _b.viewAsData, initialViewAsData = _b.initialViewAsData, setViewAsData = _b.setViewAsData;
    var _c = useRbac(), canI = _c.canI, roles = _c.roles;
    var canViewMyNotes = canI(Actions.View, Subjects.NotesMyNotes);
    var canViewScheduling = canI(Actions.View, Subjects.AppointmentsMyAppointments) ||
        (canI(Actions.View, Subjects.AppointmentsAllAppointments) && viewAsEnabled);
    var canUseViewAs = canI(Actions.View, Subjects.AppointmentsAllAppointments);
    var canViewMemberSearch = canI(Actions.View, Subjects.ClientsMyClients) || canI(Actions.View, Subjects.ClientsAllClients);
    var canEditPrescriptions = useCanIEditPrescriptions();
    // check if the roles array has the role 'med_manager''
    var canViewMyMemberNotes = (roles.includes(Roles.MedManager) && medManagerNotesFlagEnabled && canViewMyNotes) ||
        (!roles.includes(Roles.MedManager) && canViewMyNotes);
    var _d = useDisclosure(), isLogoutOpen = _d.isOpen, onLogoutOpen = _d.onOpen, onLogoutClose = _d.onClose;
    /**
     * The items to be rendered in the sidebar
     */
    var navItems = [];
    if (canViewMyMemberNotes) {
        navItems.push({
            icon: Document,
            activeIcon: DocumentFilled,
            title: 'Notes',
            url: '/notes/upcoming',
            isActive: isNotesRoute,
            isDisabled: viewAsEnabled,
            isPinnedToBottom: false,
        });
    }
    if (schedulingFlagEnabled && canViewScheduling) {
        navItems.push({
            icon: Calendar,
            activeIcon: CalendarFilled,
            title: 'Schedule',
            url: '/schedule',
            isActive: pathname.split('/').includes('schedule') && !pathname.split('/').includes('settings'),
            isPinnedToBottom: false,
        });
    }
    if (enableMemberSearch && canViewMemberSearch) {
        navItems.push({
            icon: MemberList,
            activeIcon: MemberListFilled,
            title: 'Clients',
            url: '/clients',
            isActive: pathname.split('/').includes('clients'),
            isPinnedToBottom: false,
        });
    }
    var canViewErxNavItem = enableMedicationsTab && canEditPrescriptions;
    var erxNavItem = useErxNavItem(pathname, canViewErxNavItem);
    if (canViewErxNavItem) {
        navItems.push(erxNavItem);
    }
    if (providerPayEnabled) {
        navItems.push({
            icon: Financial,
            activeIcon: FinancialFilled,
            title: 'Pay',
            url: '/pay',
            isActive: pathname.split('/').includes('pay'),
            isPinnedToBottom: true,
        });
    }
    var zdFlagEnabled = useFlagEnabled('AUTHENTICATE_ZENDESK_JWT');
    navItems.push(zenDeskSidebarItem(zdFlagEnabled));
    if (settingsFlagEnabled) {
        navItems.push({
            icon: Settings,
            activeIcon: SettingsFilled,
            title: 'Settings',
            url: '/settings/profile',
            isActive: pathname.split('/').includes('settings'),
            isPinnedToBottom: true,
        });
    }
    var _e = useComponentVisible(false), ref = _e.ref, isViewAsSearchVisible = _e.isComponentVisible, setIsViewAsSearchVisible = _e.setIsComponentVisible;
    var sideNavRef = useRef(null);
    // redirects to scheduling if view as is enabled and user is currently on notes route
    useEffect(function () {
        if (isNotesRoute && viewAsEnabled) {
            navigate('/schedule');
        }
    }, [viewAsEnabled, isNotesRoute]);
    if (viewAsFlagEnabled && canUseViewAs) {
        navItems.push({
            icon: Eye,
            activeIcon: Eye,
            title: 'View As',
            onclick: function () {
                setIsViewAsSearchVisible(!isViewAsSearchVisible);
            },
            isActive: false,
            isPinnedToBottom: true,
        });
    }
    var setViewAsUser = function (user) {
        var viewAsDataToStore = {
            id: user.id,
            careProviderId: user.care_provider.id,
            name: user.name,
            timeZone: user.care_provider.time_zone,
        };
        setViewAsData(viewAsDataToStore);
        setIsViewAsSearchVisible(false);
        window.location.reload();
    };
    var buttonHandler = function (e) {
        if (e.cancelable) {
            e.preventDefault();
        }
        setViewAsData(initialViewAsData);
        navigate('/');
    };
    // End View-As button
    // --------------------
    navItems.push({
        icon: SignOut,
        activeIcon: SignOut,
        title: 'Sign Out',
        onclick: onLogoutOpen,
        isActive: isLogoutOpen,
        isPinnedToBottom: true,
    });
    // the default application UI
    var defaultApplication = (_jsxs(_Fragment, { children: [_jsx(Logout, { isLogoutOpen: isLogoutOpen, onLogoutClose: onLogoutClose }), _jsx(Box, __assign({ bgColor: "gray.300" }, { children: _jsxs(Flex, __assign({ maxW: "container.xl", minW: "container.md", bgColor: "#ffffff", padding: 0, margin: "auto" }, { children: [_jsx(PrintWrapper, __assign({ hideWhenPrint: true }, { children: _jsx(SideNav, { innerRef: sideNavRef, navItems: navItems, viewAsEnabled: viewAsEnabled }) })), _jsx(Box, __assign({ flexGrow: 1, pos: "relative" }, { children: _jsx(IFramePageProvider, { children: _jsx(PowerPanelProvider, { children: _jsxs(Container, __assign({ maxW: "container.xl", pl: 4, pr: 4 }, { children: [_jsx(React.Suspense, __assign({ fallback: _jsx("div", {}) }, { children: _jsx(Outlet, {}) })), _jsx(IFrameOverlay, {})] })) }) }) }))] })) })), _jsx("div", __assign({ ref: ref }, { children: isViewAsSearchVisible && _jsx(ViewAsInputBox, { setViewAsUser: setViewAsUser }) }))] }));
    // if "Viewing as", return a frame around the application
    if (viewAsEnabled) {
        return (_jsx(ViewAsFrame, __assign({ fullname: viewAsData.name, buttonHandler: buttonHandler, timeZone: viewAsData.timeZone }, { children: defaultApplication })));
    }
    // no "Act-as" frame around the application
    return defaultApplication;
};
